import { useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import * as z from 'zod';
import { EMPTY_ARRAY } from '../../../../../shared/constants';
import { useAddress } from '../../../api/use-address';
import { useAddressSearch } from '../../../api/use-address-search';
import { FadeInOut } from '../../../components/fade-in-out';
import { ProgressLayout } from '../../../components/layouts/progress-layout';
import { PatchInviteForm } from '../../../components/patch-invite-form';
import { Autocomplete, makeAutocompleteHook } from '../../../components/ui/autocomplete';
import { EditCard } from '../../../components/ui/edit-card';
import { Input } from '../../../components/ui/input';
import { useInviteStore } from '../../../store/use-invite-store';

export const schema = z.object({
  id: z.string().optional(),
  building: z.string().optional(),
  street: z.string().nonempty(),
  suburb: z.string().optional(),
  city: z.string().nonempty(),
  postcode: z.string().nonempty(),
});

function extractDefaults({ patient }) {
  return {
    id: patient.address?.id ?? '',
    building: patient.address?.building ?? '',
    street: patient.address?.street ?? '',
    suburb: patient.address?.suburb ?? '',
    city: patient.address?.city ?? '',
    postcode: patient.address?.postcode ?? '',
  };
}

function transformValues(values) {
  return {
    patient: { address: values },
  };
}

export function HomeAddress(props) {
  const { invite } = useInviteStore();
  const [editing, setEditing] = useState(!invite.onlineBookingData.patient?.address?.id);

  return (
    <PatchInviteForm
      extractDefaults={extractDefaults}
      transformValues={transformValues}
      schema={schema}
      nextPath={props.next.path}
    >
      <ProgressLayout {...props}>
        <Title />
        <FadeInOut show={editing} unmount={false}>
          <FormFields setEditing={setEditing} />
        </FadeInOut>
        <FadeInOut show={!editing}>
          <AddressDetails setEditing={setEditing} />
        </FadeInOut>
      </ProgressLayout>
    </PatchInviteForm>
  );
}

function Title() {
  const { t } = useTranslation('page.details');
  const id = useWatch({ name: 'id' });
  return (
    <>
      <h1 className="title">{t('home-address.title')}</h1>
      <p className="mb-5">{t(id ? 'home-address.textSecondary' : 'home-address.text')}</p>
    </>
  );
}

function AddressDetails({ setEditing }) {
  const [building, street, suburb, city, postcode] = useWatch({
    name: ['building', 'street', 'suburb', 'city', 'postcode'],
  });
  return (
    <EditCard onClick={() => setEditing(true)} className="title-sm">
      {street && city ? (
        <pre className="font-sans leading-normal">{`${building ? `${building}, ` : ''}${street}\n${suburb ? `${suburb}, ` : ''}${city} ${postcode}`}</pre>
      ) : (
        <div className="max-w-full animate-pulse space-y-4 w-full">
          <div className="block w-3/4 h-2 bg-gray-300 rounded-full text-inherit">&nbsp;</div>
          <div className="block w-3/4 h-2 bg-gray-300 rounded-full text-inherit">&nbsp;</div>
        </div>
      )}
    </EditCard>
  );
}

function useAddressSearchAutocompleteInternal(query) {
  const state = useAddressSearch(query);
  const result = useMemo(
    () =>
      !!query && !state.loading > 0
        ? [
            ...(state.result?.map(({ id, label }) => ({ id, label })) ?? []),
            {
              id: 'manual',
              label: 'Enter manually',
              element: (
                <Trans
                  i18nKey="home-address.manually"
                  ns="page.details"
                  components={{
                    PrimaryText: (
                      <span className="font-semibold text-primary group-data-[hovered=true]/item:text-white group-data-[focused=true]/item:text-white" />
                    ),
                  }}
                />
              ),
            },
          ]
        : EMPTY_ARRAY,
    [query, state.loading, state.result],
  );
  return { ...state, result };
}
const useAddressSearchAutocomplete = makeAutocompleteHook(useAddressSearchAutocompleteInternal);

function FormFields({ setEditing }) {
  const { t } = useTranslation('page.details');
  const { setValue, watch } = useFormContext();
  const id = watch('id');
  const searchState = useAddressSearchAutocomplete();
  const [addressId, setAddressId] = useState(null);
  const addressState = useAddress(addressId, {
    onSuccess: ({ success, building, suburb, city, street, number, postcode }) => {
      if (success) {
        setValue('id', addressId, { shouldValidate: true, shouldDirty: true });
        setValue('building', building, { shouldValidate: true, shouldDirty: true });
        setValue('street', `${number} ${street}`, { shouldValidate: true, shouldDirty: true });
        setValue('suburb', suburb, { shouldValidate: true, shouldDirty: true });
        setValue('city', city, { shouldValidate: true, shouldDirty: true });
        setValue('postcode', String(postcode), { shouldValidate: true, shouldDirty: true });
      }
    },
  });
  const disabled = searchState.loading || addressState.loading;

  return (
    <div className="group space-y-4" data-has-address={!!id}>
      <Autocomplete
        {...searchState.autocompleteProps}
        label="Search address"
        placeholder={t('home-address.searchPlaceholder')}
        name="address"
        onSelect={(id) => {
          if (id) {
            setValue('id', id, { shouldValidate: true });
            setValue('street', '', { shouldValidate: true });
            setValue('suburb', '', { shouldValidate: true });
            setValue('city', '', { shouldValidate: true });
            setValue('postcode', '', { shouldValidate: true });
            if (id === 'manual') {
              setEditing(true);
              setAddressId('');
            } else if (id) {
              setAddressId(id);
              setEditing(false);
            }
            searchState.autocompleteProps.onInputChange('');
          }
        }}
        autoFocus
      >
        {({ label, element }) => (
          <Autocomplete.Item textValue={label}>{element ?? label}</Autocomplete.Item>
        )}
      </Autocomplete>
      <div className="group-data-[has-address=false]:hidden">
        <Input name="building" label={t('home-address.building')} optional disabled={disabled} />
      </div>
      <div className="group-data-[has-address=false]:hidden">
        <Input name="street" label={t('home-address.street')} disabled={disabled} />
      </div>
      <div className="group-data-[has-address=false]:hidden">
        <Input name="suburb" label={t('home-address.suburb')} optional disabled={disabled} />
      </div>
      <div className="group-data-[has-address=false]:hidden">
        <Input name="city" label={t('home-address.city')} disabled={disabled} />
      </div>
      <div className="group-data-[has-address=false]:hidden">
        <Input name="postcode" label={t('home-address.postcode')} disabled={disabled} />
      </div>
    </div>
  );
}
